import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/blog")
            .then((r) => setPosts(r.data?.posts || []))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <SEO
                title="Blog vidéo — Coulisses & conseils podcast · Studio Mon Séraphin, Villiers-sur-Marne"
                description="Nos vidéos courtes : coulisses du studio podcast de Villiers-sur-Marne (Paris Est, RER A & E), conseils de création podcast et extraits de sessions, commentés avec précision par l'équipe Mon Séraphin."
                path="/blog"
            />
            <Navbar />
            <main className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 md:px-12" data-testid="blog-page">
                <div className="max-w-7xl mx-auto">
                    <span className="ornament font-body text-xs uppercase tracking-[0.5em] text-gold-gradient">
                        Blog vidéo
                    </span>
                    <h1 className="mt-5 font-display text-4xl md:text-5xl font-extralight tracking-tight text-white leading-[1.05]">
                        Nos vidéos,
                        <br />
                        <span className="font-serif-luxe italic text-[1.05em] text-gold-gradient">
                            commentées.
                        </span>
                    </h1>
                    <p className="mt-5 text-lg text-zinc-400 font-body max-w-2xl">
                        Formats courts, coulisses du studio et conseils concrets pour créateurs,
                        commerçants et entrepreneurs — chaque vidéo accompagnée d'un commentaire
                        précis de l'équipe.
                    </p>

                    {loading ? (
                        <p className="mt-16 text-zinc-500 font-body" data-testid="blog-loading">Chargement…</p>
                    ) : posts.length === 0 ? (
                        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="blog-empty">
                            <p className="font-display text-xl text-white">Les premières vidéos arrivent très bientôt.</p>
                            <p className="mt-2 text-sm text-zinc-500 font-body">
                                Revenez dans quelques jours — les coulisses du studio se préparent.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                            {posts.map((p, i) => (
                                <motion.article
                                    key={p.id}
                                    initial={{ opacity: 0, y: 25 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: i * 0.06 }}
                                >
                                    <Link
                                        to={`/blog/${p.slug}`}
                                        className="group block h-full rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden hover:border-amber-300/40 transition-colors"
                                        data-testid={`blog-card-${p.slug}`}
                                    >
                                        <div className="relative aspect-video bg-white/[0.03] overflow-hidden">
                                            {p.thumbnail_url ? (
                                                <img
                                                    src={p.thumbnail_url}
                                                    alt={p.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Play className="w-10 h-10 text-amber-300/60" strokeWidth={1.5} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-body">
                                                {format(new Date(p.created_at), "d MMMM yyyy", { locale: fr })}
                                            </p>
                                            <h2 className="mt-2 font-display text-xl text-white font-medium group-hover:text-amber-300 transition-colors">
                                                {p.title}
                                            </h2>
                                            <p className="mt-2 text-sm text-zinc-400 font-body line-clamp-3">
                                                {p.comment}
                                            </p>
                                            <span className="mt-4 inline-flex items-center gap-1 text-xs text-amber-300 font-body">
                                                Voir la vidéo <ArrowRight className="w-3 h-3" />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
